import { useState, type FormEvent } from "react";
import { sincronizarSenhaComMemberkit } from "../lib/memberkit";

// Pede a senha ao aluno que está logado e cuja sincronização com o Memberkit
// está pendente.
//
// Existe por causa do caminho MODAL do recadastro, que a revisão de 12/08
// apontou: no "Primeiro acesso" o aluno digita a senha, mas ainda não tem
// sessão — não dá para chamar uma function autenticada. Ele clica no link do
// e-mail, o cliente do Supabase troca o token da URL por sessão, e ele cai na
// tela inicial **já logado, sem nunca ter passado pelo formulário de entrada**.
// A senha em texto nunca esteve disponível num momento em que houvesse sessão.
//
// As alternativas eram piores: guardar a senha em storage até a confirmação
// (senha em texto no navegador, por tempo indeterminado), mandá-la em
// `options.data` do signUp (grava senha no metadata do usuário) ou deslogar o
// aluno para forçar um login (atrito e confusão logo no primeiro acesso).
// Pedir uma vez, explicando para quê, é o único caminho que não guarda senha em
// lugar nenhum.
//
// Some sozinho: quando a sincronização conclui, `mk_senha_em` deixa de ser nulo.
//
// A reescrita das telas da Fase 3 apagou a vitrine antiga, que era o único lugar
// que montava este banner, e o deixou órfão por algumas horas — hoje quem o
// monta é a tela inicial, alimentada por `hub.senhaPendenteNasAulas`. Se este
// componente voltar a ficar sem ponto de montagem, a feature morre em silêncio
// outra vez: quem confirma pelo link nunca mais tem a senha pedida.
export default function LiberarAulas({ aoConcluir }: { aoConcluir: () => void }) {
  const [aberto, setAberto] = useState(false);
  const [senha, setSenha] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setOcupado(true);
    const resultado = await sincronizarSenhaComMemberkit(senha, "login");
    setOcupado(false);
    if (resultado === "ok") {
      setSenha("");
      aoConcluir();
    } else {
      setErro(
        resultado === "senha-recusada"
          ? "Essa senha não foi aceita. Use a mesma senha com que você entra aqui."
          : "Não conseguimos falar com a plataforma de aulas agora. Tentaremos de novo no seu próximo acesso.",
      );
    }
  }

  return (
    <div className="surface border-laranja/45 p-5 shadow-[0_2px_6px_rgba(255,153,51,0.08),0_14px_32px_-18px_rgba(255,153,51,0.35)]">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-3.5">
          <span className="grid size-11 shrink-0 place-items-center rounded-[13px] bg-tile text-laranja-texto">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="size-[23px]" aria-hidden="true">
              <path d="M21 2l-2 2m-7.6 7.6a5 5 0 1 1-7.1 7.1 5 5 0 0 1 7.1-7.1zm0 0L15.9 7m0 0l3 3L22 7l-3-3" />
            </svg>
          </span>
          <div>
            <h2 className="font-display font-semibold text-titulo">Falta liberar o seu acesso às aulas</h2>
            <p className="mt-0.5 text-sm text-cinza">
              Confirme a sua senha para usarmos a mesma na plataforma de aulas.
            </p>
          </div>
        </div>
        {!aberto && (
          <button onClick={() => setAberto(true)} className="pill-cta min-h-11 text-sm">
            Confirmar senha
          </button>
        )}
      </div>

      {aberto && (
        <form onSubmit={enviar} className="mt-4 flex flex-wrap items-start gap-3">
          <label className="min-w-56 flex-1">
            <span className="sr-only">Sua senha</span>
            <input
              type="password"
              required
              minLength={8}
              autoComplete="current-password"
              placeholder="A mesma senha com que você entrou"
              value={senha}
              onChange={(ev) => setSenha(ev.target.value)}
              className="h-12 w-full rounded-xl border-[1.5px] border-borda bg-superficie px-4 text-tinta transition placeholder:text-cinza/70 focus:border-laranja focus:ring-3 focus:ring-laranja/25 focus:outline-none"
            />
          </label>
          <button type="submit" disabled={ocupado} className="pill-cta h-12">
            {ocupado ? "Liberando…" : "Liberar"}
          </button>
          {erro && (
            <p role="alert" className="w-full rounded-xl bg-erro-fundo p-3.5 text-sm text-erro-tinta">
              {erro}
            </p>
          )}
        </form>
      )}
    </div>
  );
}
